import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Instagram, ArrowUpRight, Play } from "lucide-react";
import { Reveal } from "./Reveal";
import { useLang, INSTAGRAM, IMG } from "../lib/i18n";

const TILES = [IMG.realFace, IMG.realMassage, IMG.realDark, IMG.realBack, IMG.realBody, IMG.cabina].map((src) => ({ src, href: INSTAGRAM, alt: "Gioia Centro Estetico su Instagram" }));

const useFeed = () => {
  const [tiles, setTiles] = useState(TILES);
  useEffect(() => {
    if (!process.env.REACT_APP_BACKEND_URL) return undefined;
    let alive = true;
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/instagram/feed`)
      .then((r) => {
        const posts = Array.isArray(r.data?.posts) ? r.data.posts : [];
        if (alive && posts.length >= 6) {
          setTiles(posts.slice(0, 6).map((p) => ({
            src: p.media_type === "VIDEO" ? p.thumbnail_url ?? p.media_url : p.media_url ?? p.thumbnail_url,
            href: p.permalink ?? INSTAGRAM,
            alt: p.caption?.slice(0, 120) ?? "Post Instagram di Gioia Centro Estetico",
            video: p.media_type === "VIDEO",
          })));
        }
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);
  return tiles;
};

export const InstagramStrip = () => {
  const { lang } = useLang();
  const tiles = useFeed();
  const t = lang === "en"
    ? { over: "Instagram", title: "Behind the scenes, every day", cta: "Follow us on Instagram" }
    : { over: "Instagram", title: "Dietro le quinte, ogni giorno", cta: "Seguici su Instagram" };
  return (
    <section id="instagram" data-testid="instagram-section" className="bg-[#ECE5DA] py-24 sm:py-32">
      <div className="max-w-[1440px] mx-auto px-5 sm:px-10">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-8 mb-12 sm:mb-16">
          <div>
            <Reveal><p className="overline text-[#8A6A3A] mb-6 flex items-center gap-3"><Instagram className="w-4 h-4" strokeWidth={1.4} /> {t.over}</p></Reveal>
            <Reveal delay={0.05}>
              <h2 className="font-serif-d font-light text-[2.4rem] sm:text-6xl leading-[1] text-[#14110F]">{t.title}</h2>
              <a href={INSTAGRAM} target="_blank" rel="noreferrer" data-testid="instagram-handle-link" className="mt-4 inline-block font-serif-d italic text-2xl sm:text-3xl text-[#8A6A3A] hover:text-[#14110F] transition-colors duration-300">@gioia.centroestetico</a>
            </Reveal>
          </div>
          <Reveal delay={0.1}>
            <a href={INSTAGRAM} target="_blank" rel="noreferrer" data-testid="instagram-follow-btn" className="group inline-flex items-center gap-3 rounded-full bg-[#14110F] text-[#F6F2EC] px-7 py-3.5 text-[0.72rem] tracking-[0.2em] uppercase transition-colors duration-500 hover:bg-[#8A6A3A]">
              <Instagram className="w-4 h-4" /> {t.cta} <ArrowUpRight className="w-4 h-4 transition-transform duration-500 group-hover:rotate-45" />
            </a>
          </Reveal>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-3">
          {tiles.map((tile, i) => (
            <motion.a
              key={tile.src}
              href={tile.href}
              target="_blank"
              rel="noreferrer"
              data-testid={`instagram-tile-${i}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.8, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] }}
              className="group relative block aspect-square overflow-hidden bg-[#14110F]"
            >
              <img src={tile.src} alt={tile.alt} loading="lazy" className="absolute inset-0 w-full h-full object-cover object-[50%_35%] transition-[transform,opacity] duration-[1200ms] ease-out group-hover:scale-110 group-hover:opacity-60" />
              {tile.video && <Play className="absolute top-3 right-3 w-4 h-4 text-[#F6F2EC] fill-[#F6F2EC]" />}
              <span className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                <Instagram className="w-8 h-8 text-[#F6F2EC]" strokeWidth={1.3} />
              </span>
            </motion.a>
          ))}
        </div>
      </div>
    </section>
  );
};
